// Filter origins based on active filters
export const filterOrigins = (origins, activeFilters) => {
  if (!origins || activeFilters.length === 0) {
    return origins;
  }

  // Check if there's a visitors filter - it needs special handling
  const visitorsFilter = activeFilters.find(f => f.type === 'visitors');
  let filteredOrigins = origins;

  // Apply visitors filter first if present (needs to work on full dataset)
  if (visitorsFilter) {
    filteredOrigins = filterByVisitors(origins, visitorsFilter.values);
  }

  // Apply all other filters
  return filteredOrigins.filter(origin => {
    // Origin must pass ALL active filters (AND logic)
    return activeFilters.every(filter => {
      switch (filter.type) {
        case 'profile':
          return filterByProfile(origin, filter.values);

        case 'socialGrade':
          return filterBySocialGrade(origin, filter.values);

        case 'tenure':
          return filterByTenure(origin, filter.values);

        case 'employment':
          return filterByEmployment(origin, filter.values);

        case 'education':
          return filterByEducation(origin, filter.values);

        case 'imd':
          return filterByIMD(origin, filter.values);

        case 'population':
          return filterByPopulation(origin, filter.values);

        case 'visitors':
          // Already handled above
          return true;

        case 'spendPerPerson':
          return filterBySpendPerPerson(origin, filter.values);

        default:
          return true;
      }
    });
  });
};

// Filter by Profile (age and income combinations)
const filterByProfile = (origin, filterValues) => {
  const { ageProfiles, incomeProfiles } = filterValues;

  // If no selections, pass through
  if ((!ageProfiles || ageProfiles.length === 0) && (!incomeProfiles || incomeProfiles.length === 0)) {
    return true;
  }

  // Check if age profile matches (life_stage)
  const ageMatch = !ageProfiles || ageProfiles.length === 0 ||
    ageProfiles.includes(origin.life_stage);

  // Check if income profile matches (affluence)
  const incomeMatch = !incomeProfiles || incomeProfiles.length === 0 ||
    incomeProfiles.includes(origin.affluence);

  // Both must match
  return ageMatch && incomeMatch;
};

// Filter by Social Grade
const filterBySocialGrade = (origin, filterValues) => {
  const { grades } = filterValues;

  if (!grades || grades.length === 0) {
    return true;
  }

  return grades.includes(origin.dominant_social_grade);
};

// Filter by Tenure
const filterByTenure = (origin, filterValues) => {
  const { tenureTypes } = filterValues;

  if (!tenureTypes || tenureTypes.length === 0) {
    return true;
  }

  return tenureTypes.includes(origin.dominant_tenure);
};

// Filter by Employment
const filterByEmployment = (origin, filterValues) => {
  const { employmentTypes } = filterValues;

  if (!employmentTypes || employmentTypes.length === 0) {
    return true;
  }

  return employmentTypes.includes(origin.dominant_employment);
};

// Filter by Education (range slider)
const filterByEducation = (origin, filterValues) => {
  const { minLevel, maxLevel } = filterValues;

  // Education levels in order
  const educationOrder = [
    'No Qualifications',
    'Level 1',
    'Level 2',
    'Apprenticeship',
    'Level 3',
    'Level 4+'
  ];

  const originIndex = educationOrder.indexOf(origin.dominant_education);

  // If origin has no education data, exclude it
  if (originIndex === -1) {
    return false;
  }

  return originIndex >= minLevel && originIndex <= maxLevel;
};

// Filter by IMD Decile (range slider 1-10)
const filterByIMD = (origin, filterValues) => {
  const { minDecile, maxDecile } = filterValues;

  // If origin has no IMD data, exclude it
  if (!origin.imd_decile) {
    return false;
  }

  return origin.imd_decile >= minDecile && origin.imd_decile <= maxDecile;
};

// Filter by Population (range slider)
const filterByPopulation = (origin, filterValues) => {
  const { minPopulation, maxPopulation } = filterValues;

  // If origin has no population data, exclude it
  if (!origin.population) {
    return false;
  }

  return origin.population >= minPopulation && origin.population <= maxPopulation;
};

// Filter by Visitors (cumulative percentage)
const filterByVisitors = (origins, filterValues) => {
  const { percentage = 100 } = filterValues;

  // If 100%, return all origins
  if (percentage >= 100) {
    return origins;
  }

  // Sort origins by total_occurences descending
  const sortedOrigins = [...origins].sort((a, b) => b.total_occurences - a.total_occurences);

  // Calculate total visitors across all origins
  const totalVisitors = sortedOrigins.reduce((sum, origin) => sum + (origin.total_occurences || 0), 0);

  // Calculate cumulative threshold
  const threshold = (percentage / 100) * totalVisitors;

  // Select origins that contribute to the first X% of visitors
  let cumulativeVisitors = 0;
  const selectedPostcodes = new Set();

  for (const origin of sortedOrigins) {
    if (cumulativeVisitors >= threshold) {
      break;
    }
    selectedPostcodes.add(origin.postcode_sector);
    cumulativeVisitors += origin.total_occurences || 0;
  }

  // Return origins that are in the selected set
  return origins.filter(origin => selectedPostcodes.has(origin.postcode_sector));
};

// Filter by Spend Per Person (category + range)
const filterBySpendPerPerson = (origin, filterValues) => {
  const { selectedCategory, minSpend, maxSpend } = filterValues;

  // If no category selected, pass through
  if (!selectedCategory) {
    return true;
  }

  // If origin has no spend data, exclude it
  if (!origin.spend_per_pers_by_category) {
    return false;
  }

  // Parse spend data
  let spendData;
  try {
    spendData = typeof origin.spend_per_pers_by_category === 'string'
      ? JSON.parse(origin.spend_per_pers_by_category)
      : origin.spend_per_pers_by_category;
  } catch (e) {
    return false;
  }

  // Check if the selected category exists and is within range
  const categorySpend = spendData[selectedCategory];

  if (categorySpend === undefined || categorySpend === null) {
    return false;
  }

  return categorySpend >= minSpend && categorySpend <= maxSpend;
};
